/**
 * Scheduled voice/video sessions, office-hours style (SCH-621).
 *
 *   GET    /v1/calls/sessions                   member: sessions I host or am invited to (upcoming + live)
 *   POST   /v1/calls/sessions                   staff (faculty, staff, admin): schedule one
 *   DELETE /v1/calls/sessions/:sessionId        host or school admin: cancel
 *   POST   /v1/calls/sessions/:sessionId/join   host or invitee, inside the join window
 *
 * Invitees come from a chat channel the host belongs to (read server-side) or
 * an explicit list. The call matrix (decisions doc D2) applies when scheduling
 * and again at join. The first join creates the Stream call server-side; later
 * joins get the same call. A session is joinable from
 * {@link SCHEDULED_SESSION_EARLY_JOIN_MINUTES} before its start until its end.
 */

import { z } from 'zod'
import { CallPersonRefSchema } from './call-audit'
import { ChannelCidSchema } from './create-call'

export const SCHEDULED_SESSION_EARLY_JOIN_MINUTES = 10
export const SCHEDULED_SESSION_MAX_MINUTES = 240
export const SCHEDULED_SESSION_MAX_INVITEES = 99

export const ScheduledSessionStatusSchema = z.enum(['scheduled', 'live', 'ended', 'cancelled'])
export type ScheduledSessionStatus = z.infer<typeof ScheduledSessionStatusSchema>

export const ScheduledSessionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  host: CallPersonRefSchema,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  video: z.boolean(),
  channelCid: z.string().nullable(),
  status: ScheduledSessionStatusSchema,
  invitees: z.array(CallPersonRefSchema),
  /** Set once someone joined: the Stream call cid. */
  callCid: z.string().nullable(),
  /** The viewer may join right now. */
  joinable: z.boolean(),
  /** The viewer hosts it. */
  isHost: z.boolean()
})
export type ScheduledSession = z.infer<typeof ScheduledSessionSchema>

export const ListScheduledSessionsResponseSchema = z.object({
  message: z.string(),
  data: z.object({ sessions: z.array(ScheduledSessionSchema) })
})
export type ListScheduledSessionsResponse = z.infer<typeof ListScheduledSessionsResponseSchema>

export const CreateScheduledSessionRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).optional(),
    startsAt: z.string().datetime(),
    durationMinutes: z.number().int().min(5).max(SCHEDULED_SESSION_MAX_MINUTES),
    video: z.boolean().default(false),
    /** Invite the members of this chat channel (the host must be one). */
    channelCid: ChannelCidSchema.optional(),
    /** Or invite these people (UsersOnSchools.publicId). */
    inviteeIds: z.array(z.string().min(1)).max(SCHEDULED_SESSION_MAX_INVITEES).optional()
  })
  .refine(v => !!v.channelCid !== !!(v.inviteeIds && v.inviteeIds.length > 0), {
    message: 'Provide exactly one of channelCid or inviteeIds'
  })
export type CreateScheduledSessionRequest = z.infer<typeof CreateScheduledSessionRequestSchema>

export const ScheduledSessionResponseSchema = z.object({
  message: z.string(),
  data: ScheduledSessionSchema
})
export type ScheduledSessionResponse = z.infer<typeof ScheduledSessionResponseSchema>

export const ScheduledSessionIdParamsSchema = z.object({ sessionId: z.string().min(1).max(64) })
export type ScheduledSessionIdParams = z.infer<typeof ScheduledSessionIdParamsSchema>

export const JoinScheduledSessionResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    callType: z.literal('default'),
    callId: z.string(),
    callCid: z.string(),
    video: z.boolean(),
    recording: z.boolean()
  })
})
export type JoinScheduledSessionResponse = z.infer<typeof JoinScheduledSessionResponseSchema>
